import {
  InstallmentAmountSelector,
  InstallmentPeriodSelector,
  InstallmentCosts,
  InstallmentDetails,
  DerogationWarning,
} from '@/features/application/components';
import styles from './InstallmentCreditCalculatorPage.module.scss';
import { useLoanOffer } from '@/features/offer';
import type { CreditOffer } from '@/features/offer/types';
import { useApplication } from '../../hooks/useApplication';
import {
  calculateInstallmentLoanDetails,
  getMaxAmountForRates,
  getMinRatesForAmount,
  isSelectedAmountDerogation,
} from '@/features/offer/hooks/loanOfferUtils';

interface InstallmentCreditCalculatorPageProps {
  // The preliminary offer, shown without the details section; the final one by
  // default.
  offer?: CreditOffer;
}

const clamp = (value: number | undefined, min: number, max: number) =>
  value === undefined ? undefined : Math.min(Math.max(value, min), max);

export function InstallmentCreditCalculatorPage({
  offer,
}: InstallmentCreditCalculatorPageProps) {
  const { installmentOffer: finalInstallmentOffer } = useLoanOffer(true);
  const installmentOffer = offer ?? finalInstallmentOffer;
  const isPreliminary = !!offer;
  const {
    installmentDraft,
    setInstallmentAmount,
    setInstallmentNumberOfRates,
    setInstallmentIsDerogation,
  } = useApplication();

  // The draft is shared by the preliminary and the final calculator, whose
  // limits can differ.
  const details = calculateInstallmentLoanDetails(
    installmentOffer,
    installmentOffer &&
      clamp(
        installmentDraft?.amount,
        installmentOffer.minAmount,
        installmentOffer.creditLimit
      ),
    installmentOffer &&
      clamp(
        installmentDraft?.numberOfRates,
        installmentOffer.minAvailableNumberOfRates ??
          installmentOffer.minNumberOfRates,
        installmentOffer.maxNumberOfRates
      )
  );

  // The amount and duration are coupled through offer.periodCreditLimits:
  // a longer duration unlocks a higher credit ceiling. Each handler figures out
  // how the other control must move, then commits the feasible pair.
  const commitSelection = (amount: number, numberOfRates: number) => {
    if (!installmentOffer) return;
    if (amount !== details?.selectedAmount) {
      setInstallmentAmount(amount);
    }
    if (numberOfRates !== details?.selectedNumberOfRates) {
      setInstallmentNumberOfRates(numberOfRates);
    }
    setInstallmentIsDerogation(
      isSelectedAmountDerogation(installmentOffer, amount, numberOfRates)
    );
  };

  const handleAmountChange = (amount: number) => {
    if (!installmentOffer) return;
    const currentRates =
      details?.selectedNumberOfRates ?? installmentOffer.numberOfRates;
    commitSelection(
      amount,
      Math.max(currentRates, getMinRatesForAmount(installmentOffer, amount))
    );
  };

  const handlePeriodChange = (numberOfRates: number) => {
    if (!installmentOffer) return;
    const currentAmount =
      details?.selectedAmount ?? installmentOffer.creditLimit;
    commitSelection(
      Math.min(
        currentAmount,
        getMaxAmountForRates(installmentOffer, numberOfRates)
      ),
      numberOfRates
    );
  };

  if (!installmentOffer) {
    return null;
  }

  return (
    <div className={styles.calculatorContent}>
      {/* Amount Selector */}
      <InstallmentAmountSelector
        creditAmount={details?.selectedAmount}
        minAmount={installmentOffer.minAmount}
        maxAmount={installmentOffer.creditLimit}
        onAmountChange={handleAmountChange}
      />

      {/* Monthly Payment Card */}
      <InstallmentPeriodSelector
        monthlyPayment={details?.monthlyPayment}
        period={details?.selectedNumberOfRates}
        onPeriodChange={handlePeriodChange}
        minPeriod={installmentOffer.minAvailableNumberOfRates ?? installmentOffer.minNumberOfRates}
        maxPeriod={installmentOffer.maxNumberOfRates}
      />

      {details?.isDerogation && <DerogationWarning />}

      <InstallmentCosts
        creditAmount={details?.selectedAmount}
        totalInterest={details?.creditPrice}
        analysisFee={details?.grantingCommission}
        totalToRepay={details?.finalPaymentAmount}
        interestRate={details?.annualInterest}
        annualRate={details?.dae}
      />

      {!isPreliminary && (
        <InstallmentDetails
          creditAmount={details?.creditAmount}
          monthlyPayment={details?.monthlyPayment}
          period={details?.selectedNumberOfRates}
        />
      )}
    </div>
  );
}
